import dayjs from 'dayjs';
import { getUserTimezone, parseUTC } from './datetime';
import { getRunNoun } from './runNoun';

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

const asViewerDate = (dateTime, timezone = getUserTimezone()) => {
  const parsed = parseUTC(dateTime);
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  return dayjs(parsed).tz(timezone);
};

export const getViewerDayKey = (dateTime, timezone = getUserTimezone()) => {
  const value = asViewerDate(dateTime, timezone);
  return value ? value.format(DAY_KEY_FORMAT) : null;
};

export const formatViewerTime = (dateTime, timezone = getUserTimezone()) => {
  const value = asViewerDate(dateTime, timezone);
  return value ? value.format('h:mm A') : 'Time TBD';
};

export const formatViewerDay = (dayKey, timezone = getUserTimezone()) => {
  if (!dayKey) return 'Date TBD';
  const value = dayjs.tz(dayKey, DAY_KEY_FORMAT, timezone);
  return value.isValid() ? value.format('dddd, MMMM D') : 'Date TBD';
};

export const mergeHostRuns = (runs = {}) => {
  const merged = new Map();
  const sessions = [...(runs.upcoming || []), ...(runs.past || [])];

  sessions.forEach((session, index) => {
    if (!session) return;
    const key = session.id || `missing-id-${index}`;
    if (!merged.has(key)) merged.set(key, session);
  });

  return [...merged.values()].sort((a, b) => {
    const aDate = parseUTC(a?.date_time);
    const bDate = parseUTC(b?.date_time);
    const aTime = aDate && !Number.isNaN(aDate.getTime()) ? aDate.getTime() : Number.POSITIVE_INFINITY;
    const bTime = bDate && !Number.isNaN(bDate.getTime()) ? bDate.getTime() : Number.POSITIVE_INFINITY;
    if (aTime !== bTime) return aTime - bTime;
    return String(a?.id || '').localeCompare(String(b?.id || ''));
  });
};

export const groupSessionsByViewerDay = (sessions = [], timezone = getUserTimezone()) => {
  const groups = new Map();
  sessions.forEach((session) => {
    const dayKey = getViewerDayKey(session?.date_time, timezone);
    if (!dayKey) return;
    if (!groups.has(dayKey)) groups.set(dayKey, []);
    groups.get(dayKey).push(session);
  });
  return groups;
};

export const getSessionTypeLabel = (session) => getRunNoun(session);

export const getHostRoleLabel = (session) => {
  const role = String(session?.host_role || '').toLowerCase();
  return ['cohost', 'co_host', 'co-host'].includes(role) ? 'Co-host' : 'Primary host';
};

export const buildClubNameMap = (clubs = []) => new Map(
  clubs.filter((club) => club?.id).map((club) => [String(club.id), club.name || 'Club run'])
);

export const getSessionClubNames = (session, clubNames = new Map()) => {
  const ids = [];
  [...(session?.run_club_ids || []), session?.run_club_id]
    .filter(Boolean)
    .forEach((id) => {
      const normalized = String(id);
      if (!ids.includes(normalized)) ids.push(normalized);
    });

  if (ids.length === 0) return [];
  const names = ids.map((id) => clubNames.get(id)).filter(Boolean);
  return names.length > 0 ? [...new Set(names)] : ['Club run'];
};

export const getSessionState = (session) => {
  const status = String(session?.status || '').toLowerCase();
  const displayStatus = String(session?.display_status || '').toLowerCase();
  const stateValues = [status, displayStatus];
  if (stateValues.some((value) => ['cancelled', 'canceled'].includes(value))) return 'cancelled';
  if (stateValues.includes('completed')) return 'completed';
  return 'active';
};

export const getPlayerCount = (session) => Number(session?.player_count || 0);

export const getSessionCapacity = (session) => {
  const maxPlayers = Number(session?.max_players || 0);
  return maxPlayers > 0 ? maxPlayers : null;
};

export const formatJoinedCapacity = (session) => {
  const playerCount = getPlayerCount(session);
  const capacity = getSessionCapacity(session);
  return capacity ? `${playerCount}/${capacity}` : `${playerCount}/-`;
};

export const isSessionFull = (session) => {
  const capacity = getSessionCapacity(session);
  return capacity !== null && getPlayerCount(session) >= capacity;
};

export const getFactualSessionState = (session) => {
  const state = getSessionState(session);
  if (state !== 'active') return state;

  const waitlistCount = Number(session?.waitlist_count || 0);
  if (waitlistCount > 0) return 'waitlist';
  if (isSessionFull(session)) return 'full';
  return getPlayerCount(session) > 0 ? 'partial' : 'open';
};

export const getSessionHealthState = getFactualSessionState;

export const getSpotsRemaining = (session) => {
  const explicit = Number(session?.spots_remaining);
  if (Number.isFinite(explicit) && explicit >= 0 && getSessionCapacity(session) !== null) return explicit;
  const capacity = getSessionCapacity(session);
  if (capacity === null) return null;
  return Math.max(capacity - getPlayerCount(session), 0);
};

export const getSpotsRemainingLabel = (session) => {
  const spots = getSpotsRemaining(session);
  const state = getSessionState(session);
  if (spots === null || spots <= 0 || state !== 'active') return null;
  return `${spots} spot${spots === 1 ? '' : 's'} left`;
};

export const getWaitlistLabel = (session) => {
  const waitlistCount = Number(session?.waitlist_count || 0);
  if (waitlistCount <= 0) return null;
  return `${waitlistCount} waitlist`;
};

export const getDayCount = (groups, dayKey) => groups.get(dayKey)?.length || 0;

export const getSessionsForDay = (groups, dayKey) => groups.get(dayKey) || [];

export const getCalendarDay = (dayKey, timezone = getUserTimezone()) => (
  dayjs.tz(dayKey, DAY_KEY_FORMAT, timezone)
);

export const getTodayKey = (timezone = getUserTimezone()) => dayjs().tz(timezone).format(DAY_KEY_FORMAT);

export const getWeekDays = (anchorDayKey, timezone = getUserTimezone()) => {
  const anchor = getCalendarDay(anchorDayKey, timezone);
  const start = anchor.startOf('week');
  return Array.from({ length: 7 }, (_, index) => {
    const day = start.add(index, 'day');
    return {
      key: day.format(DAY_KEY_FORMAT),
      weekday: day.format('ddd'),
      dayNumber: day.format('D'),
      month: day.format('MMM'),
    };
  });
};

export const shiftDayKey = (dayKey, amount, unit, timezone = getUserTimezone()) => (
  getCalendarDay(dayKey, timezone).add(amount, unit).format(DAY_KEY_FORMAT)
);

export const getMonthGridDays = (monthDayKey, timezone = getUserTimezone()) => {
  const month = getCalendarDay(monthDayKey, timezone).startOf('month');
  const gridStart = month.startOf('week');
  return Array.from({ length: 42 }, (_, index) => {
    const day = gridStart.add(index, 'day');
    return {
      key: day.format(DAY_KEY_FORMAT),
      dayNumber: day.format('D'),
      isCurrentMonth: day.month() === month.month(),
    };
  });
};

export const getNearbyMonthShortcuts = (
  anchorDayKey,
  timezone = getUserTimezone(),
  monthsAhead = 5
) => {
  const anchor = getCalendarDay(anchorDayKey, timezone).startOf('month');
  return Array.from({ length: monthsAhead + 1 }, (_, index) => {
    const month = anchor.add(index, 'month');
    return {
      key: month.format(DAY_KEY_FORMAT),
      label: month.format('MMM'),
      fullLabel: month.format('MMMM YYYY'),
    };
  });
};

export const normalizeEventChipLabel = (session, maxLength = 14) => {
  const fallback = getSessionTypeLabel(session);
  const title = String(session?.title || '').trim();
  const label = title || fallback;
  if (label.length <= maxLength) return label;
  return `${label.slice(0, Math.max(maxLength - 1, 1)).trimEnd()}…`;
};

export const getMonthChipMeta = (session, maxLabelLength = 13) => ({
  id: session.id,
  label: normalizeEventChipLabel(session, maxLabelLength),
  typeLabel: getSessionTypeLabel(session),
  type: getSessionTypeLabel(session).toLowerCase(),
  joinedCapacity: formatJoinedCapacity(session),
  factualState: getFactualSessionState(session),
});

export const getVisibleEventChips = (sessions = [], maxVisible = 2, maxLabelLength = 14) => (
  sessions.slice(0, maxVisible).map((session) => getMonthChipMeta(session, maxLabelLength))
);

export const getOverflowCount = (sessions = [], maxVisible = 2) => (
  Math.max(sessions.length - maxVisible, 0)
);

export const getAgendaHealthMeta = (session) => {
  const capacity = getSessionCapacity(session);
  const playerCount = getPlayerCount(session);
  const joinedCapacity = formatJoinedCapacity(session);
  const factualState = getFactualSessionState(session);
  const fillPercent = capacity ? Math.min(Math.round((playerCount / capacity) * 100), 100) : null;
  return {
    typeLabel: getSessionTypeLabel(session),
    joinedCapacity,
    playerCount,
    capacity,
    factualState,
    isFull: isSessionFull(session),
    spotsLabel: getSpotsRemainingLabel(session),
    waitlistLabel: getWaitlistLabel(session),
    fillPercent,
  };
};

export const getHostSessionActionMeta = (session, now = Date.now()) => {
  const start = parseUTC(session?.date_time);
  const startsInFuture = Boolean(
    start
    && !Number.isNaN(start.getTime())
    && start.getTime() > now
  );
  const activeUpcoming = getSessionState(session) === 'active' && startsInFuture;
  const spotsRemaining = getSpotsRemaining(session);

  return {
    canShare: activeUpcoming,
    canInvite: activeUpcoming && spotsRemaining !== 0,
    canCancel: activeUpcoming,
    canCheckIn: session?.check_in?.window?.available === true,
    privacyLabel: session?.is_private ? 'Private · link only' : 'Public',
    spotsRemaining,
  };
};
